import { useState, useEffect, useCallback, useRef } from "react";
import { getPlanityUrl } from "@/config/standort";
import { Menu, X, Calendar, MessageCircle, ArrowRight } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import logo from "@/assets/22-studios-beauty-salon-logo-nuernberg.png";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";


type NavLink = {
  href: string;
  label: string;
  tooltip?: string;
  isAnchor?: boolean;
  anchor?: string;
  isContact?: boolean;
};

const getNuernbergNav = (): NavLink[] => [
  { href: "/nuernberg", label: "Start", isAnchor: true, anchor: "#start" },
  { href: "/nuernberg/haare", label: "Haare & Bart", tooltip: "Haarstyling, Haarschnitt & Bartpflege" },
  { href: "/nuernberg/facials", label: "Gesichtsbehandlung", tooltip: "Gesichtsbehandlung & Treatments" },
  { href: "/nuernberg/naegel", label: "Nägel", tooltip: "Maniküre & Pediküre" },
  { href: "/nuernberg/haarentfernung", label: "Haarentfernung", tooltip: "Dauerhafte Laser-Haarentfernung" },
  { href: "/nuernberg/team", label: "Team & Studio", tooltip: "Unser Team & Studio" },
  { href: "/blog", label: "Blog", tooltip: "Beauty Ratgeber & Tipps" },
  { href: "#kontakt", label: "Kontakt", isContact: true },
];

const Navigation = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isInHero, setIsInHero] = useState(true);
  const [contactOpen, setContactOpen] = useState(false);
  const location = useLocation();
  const planityUrl = getPlanityUrl();
  const navigate = useNavigate();
  const basePath = "/nuernberg";
  const navLinks = getNuernbergNav();
  const rafRef = useRef<number>(0);
  const lastScrollY = useRef(0);

  useEffect(() => {
    let ticking = false;

    const handleScroll = () => {
      lastScrollY.current = window.scrollY;
      if (!ticking) {
        ticking = true;
        rafRef.current = requestAnimationFrame(() => {
          const y = lastScrollY.current;
          setIsScrolled(y > 50);
          setIsInHero(y < window.innerHeight * 0.7);
          ticking = false;
        });
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  const handleNavClick = useCallback((link: NavLink) => {
    setIsOpen(false);
    if (link.isContact) {
      setContactOpen(true);
      return;
    }
    if (link.isAnchor && link.anchor) {
      if (location.pathname === basePath) {
        const element = document.querySelector(link.anchor);
        if (element) {
          element.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        // Navigate to homepage first, then scroll
        navigate(basePath);
        // Scroll after navigation
        setTimeout(() => {
          const element = document.querySelector(link.anchor!);
          if (element) {
            element.scrollIntoView({ behavior: "smooth" });
          }
        }, 300);
      }
    }
  }, [basePath, location.pathname, navigate]);

  const isHomepage = location.pathname === basePath;
  const useWhiteText = isHomepage && isInHero;
  const textColorClass = useWhiteText
    ? "text-white hover:text-white/80"
    : "text-foreground hover:text-foreground/70";

  const NavItem = ({ link }: { link: NavLink }) => {
    if (link.isContact) {
      const content = (
        <button
          onClick={() => setContactOpen(true)}
          className={`text-sm font-medium tracking-wide ${textColorClass} link-underline transition-colors duration-300`}
        >
          {link.label}
        </button>
      );
      return content;
    }

    const content = link.isAnchor ? (
      <a
        href={link.anchor}
        onClick={(e) => {
          e.preventDefault();
          handleNavClick(link);
        }}
        className={`text-sm font-medium tracking-wide ${textColorClass} link-underline transition-colors duration-300`}
      >
        {link.label}
      </a>
    ) : (
      <Link
        to={link.href}
        className={`text-sm font-medium tracking-wide ${textColorClass} link-underline transition-colors duration-300`}
      >
        {link.label}
      </Link>
    );

    if (link.tooltip) {
      return (
        <Tooltip delayDuration={100}>
          <TooltipTrigger asChild>{content}</TooltipTrigger>
          <TooltipContent
            side="bottom"
            className="bg-foreground text-background text-xs px-3 py-1.5 rounded-lg shadow-lg border-0"
          >
            {link.tooltip}
          </TooltipContent>
        </Tooltip>
      );
    }

    return content;
  };

  return (
    <TooltipProvider>
      <header
        role="banner"
        className="fixed top-4 left-1/2 -translate-x-1/2 z-50 w-[calc(100%-2rem)] max-w-6xl transition-all duration-500 rounded-2xl bg-white/10 backdrop-blur-xl border border-white/20 shadow-lg will-change-transform"
      >
        <nav aria-label="Hauptnavigation" className="container flex items-center justify-between h-16 md:h-18 px-6">
          <button
            onClick={() => {
              setIsOpen(false);
              if (location.pathname === basePath) {
                window.scrollTo({ top: 0, behavior: "smooth" });
              } else {
                window.location.href = basePath;
              }
            }}
            className="flex items-center gap-3 hover-scale cursor-pointer"
          >
            <img
              src={logo}
              alt="22 Studios Logo"
              className={`h-10 md:h-12 w-auto transition-all duration-300 ${useWhiteText ? "" : "brightness-0"}`}
              width={120}
              height={48}
            />
          </button>

          <div className="hidden lg:flex items-center gap-5">
            <ul role="menubar" className="flex items-center gap-5">
              {navLinks.map((link) => (
                <li key={link.href} role="none">
                  <NavItem link={link} />
                </li>
              ))}
            </ul>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className={`lg:hidden p-2 ${useWhiteText ? "text-white hover:text-white/70" : "text-foreground hover:text-foreground/70"} transition-colors`}
            aria-label={isOpen ? "Menü schließen" : "Menü öffnen"}
          >
            {isOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>

        <div
          className={`lg:hidden overflow-hidden transition-all duration-300 ${
            isOpen ? "max-h-[500px] pb-6" : "max-h-0"
          }`}
        >
          <ul role="menu" aria-label="Mobile Navigation" className="flex flex-col items-center gap-4 pt-4">
            <li role="none" className="pb-2">
            </li>
            {navLinks.map((link) => (
              <li key={link.href} role="none">
                {link.isContact ? (
                  <button
                    onClick={() => {
                      setIsOpen(false);
                      setContactOpen(true);
                    }}
                    className="text-base font-medium text-foreground/80 hover:text-foreground transition-colors"
                  >
                    {link.label}
                  </button>
                ) : link.isAnchor ? (
                  <a
                    href={link.anchor}
                    onClick={(e) => {
                      e.preventDefault();
                      handleNavClick(link);
                    }}
                    className="text-base font-medium text-foreground/80 hover:text-foreground transition-colors flex flex-col items-center"
                  >
                    {link.label}
                    {link.tooltip && (
                      <span className="text-xs text-muted-foreground">{link.tooltip}</span>
                    )}
                  </a>
                ) : (
                  <Link
                    to={link.href}
                    onClick={() => setIsOpen(false)}
                    className="text-base font-medium text-foreground/80 hover:text-foreground transition-colors flex flex-col items-center"
                  >
                    {link.label}
                    {link.tooltip && (
                      <span className="text-xs text-muted-foreground">{link.tooltip}</span>
                    )}
                  </Link>
                )}
              </li>
            ))}
          </ul>
        </div>
      </header>

      {/* Contact Choice Dialog */}
      <Dialog open={contactOpen} onOpenChange={setContactOpen}>
        <DialogContent className="sm:max-w-md rounded-2xl p-0 overflow-hidden border-0">
          <DialogHeader className="p-6 pb-2">
            <DialogTitle className="font-serif text-xl text-center">Wie möchtest du uns erreichen?</DialogTitle>
            <DialogDescription className="text-center text-sm text-muted-foreground">
              Wähle eine Option
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3 p-6 pt-2">
            <a
              href={planityUrl}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setContactOpen(false)}
              className="group flex items-center gap-4 p-4 rounded-xl bg-charcoal text-white hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              <div className="w-11 h-11 rounded-lg bg-white/10 flex items-center justify-center group-hover:bg-white/20 transition-colors">
                <Calendar className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <p className="font-medium">Online buchen</p>
                <p className="text-white/60 text-sm">Direkt Termin über Planity wählen</p>
              </div>
              <ArrowRight className="w-4 h-4 opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </a>

            <button
              onClick={() => {
                setContactOpen(false);
                navigate(`${basePath}/anfrage`);
              }}
              className="group flex items-center gap-4 p-4 rounded-xl bg-secondary border border-border hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 text-left"
            >
              <div className="w-11 h-11 rounded-lg bg-background flex items-center justify-center group-hover:bg-primary/10 transition-colors">
                <MessageCircle className="w-5 h-5 text-foreground" />
              </div>
              <div className="flex-1">
                <p className="font-medium text-foreground">Anfrage senden</p>
                <p className="text-muted-foreground text-sm">Fragen oder spezielle Wünsche</p>
              </div>
              <ArrowRight className="w-4 h-4 text-muted-foreground opacity-50 group-hover:opacity-100 group-hover:translate-x-1 transition-all" />
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
};

export default Navigation;
